import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { adminApi, errMsg, fmtDate } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PostView } from "@/pages/Blog";
import { Btn, Field, PageHead, Panel, inputCls } from "@/admin/ui";

const EMPTY = { title: "", cover_image: "", summary: "", seo_description: "", body: "", status: "draft" };

function Editor({ post, onSaved, onDelete }) {
  const [f, setF] = useState({ ...EMPTY, ...post });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (status) => {
    setBusy(true);
    try {
      const body = { ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, f[k] ?? ""])), status };
      const r = post.id ? await adminApi.put(`/blog/${post.id}`, body) : await adminApi.post("/blog", body);
      toast.success(status === "published" ? "Published" : "Saved as draft");
      onSaved(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Panel data-testid="blog-editor">
      <Tabs defaultValue="edit">
        <TabsList className="rounded-none"><TabsTrigger value="edit" data-testid="blog-tab-edit">Edit</TabsTrigger><TabsTrigger value="preview" data-testid="blog-tab-preview">Preview</TabsTrigger></TabsList>
        <TabsContent value="edit" className="space-y-4 mt-4">
          <Field label="Title"><input value={f.title} onChange={set("title")} className={inputCls} data-testid="blog-title" /></Field>
          <Field label="Cover image URL"><input value={f.cover_image} onChange={set("cover_image")} placeholder="https://…" className={inputCls} data-testid="blog-cover" /></Field>
          <Field label="Summary"><textarea rows={2} value={f.summary} onChange={set("summary")} className={`${inputCls} h-auto py-2`} data-testid="blog-summary" /></Field>
          <Field label="SEO description"><input value={f.seo_description} onChange={set("seo_description")} className={inputCls} data-testid="blog-seo" /></Field>
          <Field label="Body" hint="Blank line = new paragraph · ## Heading · - list item · **bold** · ![alt](https://image-url)">
            <textarea rows={14} value={f.body} onChange={set("body")} className={`${inputCls} h-auto py-2 font-arial`} data-testid="blog-body" />
          </Field>
        </TabsContent>
        <TabsContent value="preview" className="mt-6 bg-[var(--vrv-cream)] p-6 border border-zinc-200" data-testid="blog-preview"><PostView post={f} /></TabsContent>
      </Tabs>
      <div className="flex flex-wrap gap-2 mt-6">
        <Btn variant="red" disabled={busy || !f.title.trim()} onClick={() => save("published")} data-testid="blog-publish">{post.status === "published" ? "Update & keep published" : "Publish"}</Btn>
        <Btn variant="outline" disabled={busy || !f.title.trim()} onClick={() => save("draft")} data-testid="blog-save-draft">{post.status === "published" ? "Unpublish (draft)" : "Save draft"}</Btn>
        {post.id && <Btn variant="ghost" className="text-red ml-auto" onClick={onDelete} data-testid="blog-delete">Delete</Btn>}
      </div>
    </Panel>
  );
}

export default function BlogAdmin() {
  const [posts, setPosts] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => adminApi.get("/blog").then((r) => setPosts(r.data));
  useEffect(() => {
    load();
  }, []);
  const del = async () => {
    if (!window.confirm("Delete this post?")) return;
    await adminApi.delete(`/blog/${edit.id}`);
    setEdit(null);
    load();
  };
  return (
    <div>
      <PageHead title="Blog" desc="Write posts, preview them and publish or keep as draft.">
        <Btn onClick={() => setEdit({ ...EMPTY })} data-testid="blog-new"><Plus className="w-4 h-4" /> New post</Btn>
      </PageHead>
      <div className="grid lg:grid-cols-[300px_1fr] gap-6 items-start">
        <Panel className="p-0 sm:p-0">
          <ul className="divide-y divide-zinc-100" data-testid="blog-admin-list">
            {posts.map((p) => (
              <li key={p.id}><button onClick={() => setEdit(p)} className={`w-full text-left px-4 py-3 hover:bg-zinc-50 ${edit?.id === p.id ? "bg-zinc-100" : ""}`} data-testid={`blog-admin-item-${p.slug}`}>
                <p className="font-bold text-sm">{p.title}</p>
                <p className="text-xs text-zinc-500 mt-0.5"><span className={p.status === "published" ? "text-emerald-700" : "text-amber-700"}>{p.status}</span> · {fmtDate(p.updated_at)}</p>
              </button></li>
            ))}
            {!posts.length && <li className="p-6 text-sm text-zinc-500">No posts yet.</li>}
          </ul>
        </Panel>
        {edit ? <Editor key={edit.id || "new"} post={edit} onSaved={(p) => { setEdit(p); load(); }} onDelete={del} /> : <Panel className="text-sm text-zinc-500">Choose a post or create a new one.</Panel>}
      </div>
    </div>
  );
}
